const MAX_BITMAP_EDGE = 320;
const SAMPLE_RADIUS = 2;

export interface Point {
  x: number;
  y: number;
}

/** Eight edge sample centers: corners + edge midpoints. */
export function edgeSamplePoints(width: number, height: number, inset = 0): Point[] {
  const w = Math.max(1, width - 1);
  const h = Math.max(1, height - 1);
  const midX = Math.floor(w / 2);
  const midY = Math.floor(h / 2);
  return [
    { x: inset, y: inset },
    { x: w - inset, y: inset },
    { x: inset, y: h - inset },
    { x: w - inset, y: h - inset },
    { x: midX, y: inset },
    { x: midX, y: h - inset },
    { x: inset, y: midY },
    { x: w - inset, y: midY },
  ];
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function componentToHex(value: number): string {
  return clampByte(value).toString(16).padStart(2, '0');
}

/** Averages RGB tuples into a #RRGGBB hex string. */
export function averageRgbToHex(samples: Array<[number, number, number]>): string | null {
  if (samples.length === 0) return null;
  let r = 0;
  let g = 0;
  let b = 0;
  for (const [sr, sg, sb] of samples) {
    r += sr;
    g += sg;
    b += sb;
  }
  const n = samples.length;
  return `#${componentToHex(r / n)}${componentToHex(g / n)}${componentToHex(b / n)}`;
}

function scaledDimensions(width: number, height: number): { width: number; height: number } {
  const maxEdge = Math.max(width, height);
  if (maxEdge <= MAX_BITMAP_EDGE) {
    return { width, height };
  }
  const scale = MAX_BITMAP_EDGE / maxEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function collectEdgeSamples(data: Uint8ClampedArray, width: number, height: number): Array<[number, number, number]> {
  const samples: Array<[number, number, number]> = [];
  for (const { x, y } of edgeSamplePoints(width, height, SAMPLE_RADIUS)) {
    for (let dy = -SAMPLE_RADIUS; dy <= SAMPLE_RADIUS; dy += 1) {
      for (let dx = -SAMPLE_RADIUS; dx <= SAMPLE_RADIUS; dx += 1) {
        const px = Math.max(0, Math.min(width - 1, x + dx));
        const py = Math.max(0, Math.min(height - 1, y + dy));
        const i = (py * width + px) * 4;
        const alpha = data[i + 3] ?? 255;
        if (alpha < 16) continue;
        samples.push([data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0]);
      }
    }
  }
  return samples;
}

export function extractEdgeColorFromImageData(data: Uint8ClampedArray, width: number, height: number): string | null {
  const samples = collectEdgeSamples(data, width, height);
  return averageRgbToHex(samples);
}

function drawBitmapToCanvas(
  bitmap: ImageBitmap | { width: number; height: number },
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
): string | null {
  const { width, height } = scaledDimensions(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  draw(ctx, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  return extractEdgeColorFromImageData(imageData.data, width, height);
}

function isClosableBitmap(source: unknown): source is ImageBitmap {
  return (
    typeof source === 'object' &&
    source !== null &&
    'close' in source &&
    typeof (source as ImageBitmap).close === 'function'
  );
}

async function decodeBlobToBitmap(blob: Blob): Promise<ImageBitmap | HTMLImageElement | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      // fall through to Image
    }
  }

  return await new Promise<HTMLImageElement | null>((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/** Samples edge pixels from an image blob and returns an average #RRGGBB color, or null on failure. */
export async function extractImageEdgeColor(blob: Blob): Promise<string | null> {
  const source = await decodeBlobToBitmap(blob);
  if (!source) return null;

  try {
    return drawBitmapToCanvas(source, (ctx, w, h) => {
      ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
    });
  } catch {
    return null;
  } finally {
    if (isClosableBitmap(source)) {
      source.close();
    }
  }
}
